import React, { useEffect, useState } from 'react';
import { ArrowRight, CalendarDays, Loader2, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getBlogs } from '../../services/blogs';

const formatDate = (value) => {
  if (!value) return 'Draft';
  return new Date(value).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

export default function BlogsPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const data = await getBlogs({ limit: 100 });
        setBlogs(data.blogs || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load blogs.');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  return (
    <section className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 pt-32 pb-20">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-14">
          <p className="text-cyan-400 uppercase tracking-[0.3em] text-sm mb-4">Insights</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
            Stories, ideas, and practical notes from our team.
          </h1>
          <p className="text-slate-300 text-lg mt-5 max-w-2xl">
            Explore published articles on technology, business, design, and growth from Algoryn.
          </p>
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-slate-200">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading blogs...</span>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-5 text-red-100">
            {error}
          </div>
        )}

        {!loading && !error && blogs.length === 0 && (
          <div className="rounded-3xl border border-white/10 bg-white/5 px-8 py-16 text-center text-slate-300">
            No published blogs yet.
          </div>
        )}

        {!loading && !error && blogs.length > 0 && (
          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {blogs.map((blog, index) => (
              <article
                key={blog._id}
                className={`group rounded-3xl border border-white/10 bg-white/5 p-7 backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:border-cyan-400/40 hover:bg-white/10 ${
                  index === 0 ? 'xl:col-span-2' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="inline-flex rounded-full bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
                    {blog.category}
                  </span>
                  {blog.featured && (
                    <span className="inline-flex rounded-full bg-amber-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-200">
                      Featured
                    </span>
                  )}
                </div>

                <h2 className="mt-6 text-2xl font-bold text-white leading-snug">
                  {blog.title}
                </h2>

                <p className="mt-4 text-slate-300 leading-7">
                  {blog.excerpt || 'Open this article to read the full post.'}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-400">
                  <span className="inline-flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />
                    {formatDate(blog.publishedAt || blog.createdAt)}
                  </span>
                  {blog.tags?.[0] && (
                    <span className="inline-flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      {blog.tags.slice(0, 2).join(', ')}
                    </span>
                  )}
                </div>

                <Link
                  to={`/blogs/${blog.slug}`}
                  className="mt-8 inline-flex items-center gap-2 text-cyan-300 transition-colors group-hover:text-cyan-200"
                >
                  Read article
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
